import { all, call, takeEvery, put, delay } from "redux-saga/effects";
import axios from "axios";
import examScheduleActions from "./actions";
import commonActions from "../common/actions";
import { API_URL } from "../../utils/constants";

/* Course-level admit-card exam schedule (cor_courseexamschedule).
   One table, two kinds of row told apart by row_type:
     SECTION — a label/value line of a "DETAILS OF CET EXAMINATION" block
     PAPER   — a column of AAC's paper-wise grid (paper_no 0 = the break column) */
const BASE_URL = `${API_URL}/master/exam-schedule`;

const ROW_TYPE = { SECTION: "SECTION", PAPER: "PAPER" };

/* Authorization failures come back as HTTP 200 with statusCode 400 in the body,
   so the body is what decides success. Validation errors are real HTTP 400/404/500
   and axios throws on those, so they are unwrapped back into the same shape. */
const callApi = (request) =>
    request()
        .then((res) => res?.data)
        .catch((err) => err?.response?.data ?? {
            statusCode: 500,
            message: "Something went wrong, please try again",
        });

const showAlert = function* (result) {
    yield put({
        type: commonActions.SET_ALERT,
        payload: {
            status: result?.statusCode === 200 ? "success" : "failed",
            show: true,
            message: result?.message,
        },
    });
    yield delay(2000);
    yield put({
        type: commonActions.SET_ALERT,
        payload: { status: null, show: false, message: null },
    });
};

/* a write only ever touches one of the two lists — reload that one plus the
   preview, which is the printed output of both */
const refreshCourse = function* (courseid, rowType) {
    if (!courseid) return;
    yield put({
        type: rowType === ROW_TYPE.PAPER
            ? examScheduleActions.GET_EXAM_PAPERS
            : examScheduleActions.GET_EXAM_SCHEDULE,
        payload: { courseid },
    });
    yield put({ type: examScheduleActions.GET_SCHEDULE_PREVIEW, payload: { courseid } });
};

const examScheduleSagas = function* () {
    yield all([
        yield takeEvery(examScheduleActions.GET_SCHEDULE_COURSES, getScheduleCourses),
        yield takeEvery(examScheduleActions.GET_EXAM_SCHEDULE, getExamSchedule),
        yield takeEvery(examScheduleActions.GET_EXAM_PAPERS, getExamPapers),
        yield takeEvery(examScheduleActions.GET_SCHEDULE_PREVIEW, getSchedulePreview),
        yield takeEvery(examScheduleActions.CREATE_SCHEDULE_ROW, createScheduleRow),
        yield takeEvery(examScheduleActions.UPDATE_SCHEDULE_ROW, updateScheduleRow),
        yield takeEvery(examScheduleActions.DELETE_SCHEDULE_ROW, deleteScheduleRow),
    ]);
};

/** GET /courses — only the courses that use a course-specific admit-card format */
const getScheduleCourses = function* () {
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: true });
    try {
        const result = yield call(() => callApi(() => axios.get(`${BASE_URL}/courses`)));
        if (result?.statusCode === 200) {
            yield put({
                type: examScheduleActions.SET_SCHEDULE_COURSES,
                payload: result?.result ?? [],
            });
        } else {
            yield call(showAlert, result);
        }
    } catch (err) {
        ;
    }
    yield put({ type: commonActions.SET_PAGE_LOADER, payload: false });
};

/** GET /:courseid — the SECTION rows */
const getExamSchedule = function* (data) {
    const { payload } = data;

    yield put({ type: commonActions.SET_TABLE_LOADER, payload: true });
    try {
        const result = yield call(() =>
            callApi(() => axios.get(`${BASE_URL}/${payload?.courseid}`))
        );
        yield put({
            type: examScheduleActions.SET_EXAM_SCHEDULE,
            payload: result?.statusCode === 200 ? result?.result ?? [] : [],
        });
        if (result?.statusCode !== 200) yield call(showAlert, result);
    } catch (err) {
        ;
    }
    yield put({ type: commonActions.SET_TABLE_LOADER, payload: false });
};

/** GET /paper/:courseid — the PAPER rows */
const getExamPapers = function* (data) {
    const { payload } = data;

    yield put({ type: commonActions.SET_TABLE_LOADER, payload: true });
    try {
        const result = yield call(() =>
            callApi(() => axios.get(`${BASE_URL}/paper/${payload?.courseid}`))
        );
        yield put({
            type: examScheduleActions.SET_EXAM_PAPERS,
            payload: result?.statusCode === 200 ? result?.result ?? [] : [],
        });
        if (result?.statusCode !== 200) yield call(showAlert, result);
    } catch (err) {
        ;
    }
    yield put({ type: commonActions.SET_TABLE_LOADER, payload: false });
};

/** GET /preview/:courseid — exactly what the admit card PDF will print */
const getSchedulePreview = function* (data) {
    const { payload } = data;

    try {
        const result = yield call(() =>
            callApi(() => axios.get(`${BASE_URL}/preview/${payload?.courseid}`))
        );
        yield put({
            type: examScheduleActions.SET_SCHEDULE_PREVIEW,
            payload: result?.statusCode === 200 ? result?.result : null,
        });
    } catch (err) {
        ;
    }
};

/** POST / — creates either row type, row_type in the body decides which */
const createScheduleRow = function* (data) {
    const { payload } = data;
    const { handleClose } = payload;

    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: true });
    try {
        const result = yield call(() => callApi(() => axios.post(`${BASE_URL}`, payload?.data)));
        if (result?.statusCode === 200) {
            yield call(refreshCourse, payload?.data?.courseid, payload?.data?.row_type);
            handleClose?.();
        }
        yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
        yield call(showAlert, result);
    } catch (err) {
        ;
    }
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
};

/** PUT /:id — the full row must be sent, omitted fields are written as NULL */
const updateScheduleRow = function* (data) {
    const { payload } = data;
    const { handleClose } = payload;

    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: true });
    try {
        const result = yield call(() =>
            callApi(() => axios.put(`${BASE_URL}/${payload?.id}`, payload?.data))
        );
        if (result?.statusCode === 200) {
            yield call(refreshCourse, payload?.data?.courseid, payload?.data?.row_type);
            handleClose?.();
        }
        yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
        yield call(showAlert, result);
    } catch (err) {
        ;
    }
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
};

/** DELETE /:id — soft delete, either row type */
const deleteScheduleRow = function* (data) {
    const { payload } = data;
    const { handleClose } = payload;

    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: true });
    try {
        const result = yield call(() =>
            callApi(() =>
                axios.delete(`${BASE_URL}/${payload?.id}`, { data: { userid: payload?.userid } })
            )
        );
        if (result?.statusCode === 200) {
            yield call(refreshCourse, payload?.courseid, payload?.rowType);
            handleClose?.();
        }
        yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
        yield call(showAlert, result);
    } catch (err) {
        ;
    }
    yield put({ type: commonActions.SET_BUTTON_LOADER, payload: false });
};

export default examScheduleSagas;
